/**
 * Client-side halves of the contact merge (api#848,
 * POST /customers/:customerId/contacts/:keepId/merge { fromId }).
 *
 * `previewContactMerge` mirrors the server's fill rule so the office can see
 * what the kept contact will look like BEFORE confirming. It is a preview of a
 * write the server makes, never a value the screen keeps: after a merge the
 * row on screen is the one the server returned.
 */

export interface MergeableContact {
  id: string;
  name: string | null;
  phone: string | null;
  email: string | null;
  role: string | null;
  rigName: string | null;
}

/** A null, or a string that is empty once trimmed — the API's `isBlank`. */
const isBlank = (v: string | null | undefined): boolean => v == null || v.trim() === '';

/**
 * The server's rule, field by field: phone, email and role on KEEP that are
 * blank take FROM's value; keep's non-blank values win. Name and rig are
 * always keep's, blank or not — the person merging picked the keeper for its
 * name and rig, so the server never fills either.
 */
export function previewContactMerge(keep: MergeableContact, from: MergeableContact): MergeableContact {
  const fill = (k: string | null, f: string | null) => (isBlank(k) && !isBlank(f) ? f : k);
  return {
    id: keep.id,
    name: keep.name,
    rigName: keep.rigName,
    phone: fill(keep.phone, from.phone),
    email: fill(keep.email, from.email),
    role: fill(keep.role, from.role),
  };
}

/** Digits only, so "(780) 221-8360" and "7802218360" compare equal. */
export const phoneDigits = (phone: string | null | undefined): string => (phone ?? '').replace(/\D/g, '');

const emailKey = (email: string | null | undefined): string => (email ?? '').trim().toLowerCase();

/**
 * Ids of contacts sharing phone digits, or a lowercased email, with another
 * contact in the list. Display only — nothing merges on this. A contact with
 * no phone and no email matches nothing: two blanks are not a shared value.
 */
export function possibleDuplicateIds(contacts: MergeableContact[]): Set<string> {
  const byKey = new Map<string, string[]>();
  const add = (key: string, id: string) => {
    const ids = byKey.get(key);
    if (ids) ids.push(id);
    else byKey.set(key, [id]);
  };
  for (const c of contacts) {
    const digits = phoneDigits(c.phone);
    if (digits) add(`phone:${digits}`, c.id);
    const email = emailKey(c.email);
    if (email) add(`email:${email}`, c.id);
  }
  const dupes = new Set<string>();
  byKey.forEach((ids) => {
    if (ids.length > 1) ids.forEach((id) => dupes.add(id));
  });
  return dupes;
}
